import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupiahFull, formatTanggalID } from "@/lib/format";
import { Plus, Edit2, Trash2, Check, Coins, FileText } from "lucide-react";
import HutangForm from "@/pages/forms/HutangForm";
import BayarHutangForm from "@/pages/forms/BayarHutangForm";
import RiwayatBayarHutang from "@/pages/RiwayatBayarHutang";
import DeleteConfirm from "@/components/DeleteConfirm";
import { toast } from "sonner";

export default function Hutang() {
  const [items, setItems] = useState([]);
  const [rekening, setRekening] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [bayarOpen, setBayarOpen] = useState(false);
  const [riwayatOpen, setRiwayatOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, r] = await Promise.all([
        api.get("/hutang"),
        api.get("/rekening"),
      ]);
      setItems(h.data);
      setRekening(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalSisa = items
    .filter((h) => h.status === "aktif")
    .reduce((a, b) => a + b.sisa, 0);

  const doDelete = async () => {
    try {
      await api.delete(`/hutang/${confirmDel.id}`);
      toast.success("Hutang dihapus");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div className="pb-40" data-testid="hutang-page">
      <div className="px-4 pt-5 pb-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500">Total sisa hutang aktif</div>
            <div
              className="text-2xl font-extrabold mt-0.5"
              style={{ color: totalSisa > 0 ? "#C00000" : "#375623" }}
              data-testid="hutang-total-sisa"
            >
              {formatRupiahFull(totalSisa)}
            </div>
          </div>
          <button
            onClick={() => setRiwayatOpen(true)}
            data-testid="riwayat-bayar-btn"
            className="h-9 px-3 rounded-full bg-white border border-slate-200 flex items-center gap-1.5 text-xs font-semibold text-[#1F4E5F] tap"
          >
            <FileText size={13} /> Riwayat
          </button>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          <div className="text-xs text-slate-500 py-8 text-center">
            Memuat...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-xs text-slate-500 border border-slate-100">
            Belum ada catatan hutang.
          </div>
        ) : (
          items.map((h) => {
            const paid = h.total - h.sisa;
            const pct = Math.min(100, Math.round((paid / h.total) * 100));
            const lunas = h.status === "lunas";
            return (
              <div
                key={h.id}
                className="rounded-2xl p-4 border border-slate-100"
                style={{ backgroundColor: lunas ? "#E2EFDA" : "#FFF2CC" }}
                data-testid={`hutang-row-${h.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-bold text-slate-900 truncate">
                        {h.deskripsi}
                      </div>
                      {lunas && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded font-bold text-white flex items-center gap-0.5"
                          style={{ backgroundColor: "#375623" }}
                        >
                          <Check size={9} /> LUNAS
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      Mulai {formatTanggalID(h.tanggal_mulai)} · Tempo{" "}
                      {formatTanggalID(h.jatuh_tempo)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-slate-500">Sisa</div>
                    <div
                      className="text-sm font-extrabold"
                      style={{ color: lunas ? "#375623" : "#C00000" }}
                    >
                      {formatRupiahFull(h.sisa)}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-slate-600 mb-1">
                    <span>Terbayar {formatRupiahFull(paid)}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/80 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: lunas ? "#375623" : "#2E75B6",
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3">
                  <button
                    onClick={() => {
                      setEditing(h);
                      setFormOpen(true);
                    }}
                    data-testid={`hutang-edit-${h.id}`}
                    className="text-[10px] px-2 py-1 rounded-md bg-white text-slate-700 font-semibold tap flex items-center gap-1"
                  >
                    <Edit2 size={10} /> Edit
                  </button>
                  <button
                    onClick={() => setConfirmDel(h)}
                    data-testid={`hutang-delete-${h.id}`}
                    className="text-[10px] px-2 py-1 rounded-md font-semibold tap flex items-center gap-1 text-white"
                    style={{ backgroundColor: "#C00000" }}
                  >
                    <Trash2 size={10} /> Hapus
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bottom buttons */}
      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-[380px] px-4 grid grid-cols-2 gap-2"
        style={{ bottom: "80px" }}
      >
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          data-testid="tambah-hutang-btn"
          className="h-12 rounded-xl bg-white border border-slate-200 text-[#1F4E5F] font-semibold text-sm tap shadow-lg flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Tambah
        </button>
        <button
          onClick={() => setBayarOpen(true)}
          data-testid="bayar-hutang-btn"
          className="h-12 rounded-xl text-white font-semibold text-sm tap shadow-lg flex items-center justify-center gap-1"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          <Coins size={14} /> Bayar Hutang
        </button>
      </div>

      <HutangForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        existing={editing}
        onSaved={load}
        rekeningList={rekening}
      />
      <BayarHutangForm
        open={bayarOpen}
        onClose={() => setBayarOpen(false)}
        hutangList={items}
        rekeningList={rekening}
        onSaved={load}
      />
      <RiwayatBayarHutang
        open={riwayatOpen}
        onClose={() => setRiwayatOpen(false)}
      />
      <DeleteConfirm
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={doDelete}
        itemName={confirmDel?.deskripsi}
        testId="delete-hutang-dialog"
      />
    </div>
  );
}
